import { supabase } from '../lib/supabase';

// Collectibles Service - CRUD operations for collectible items
export class CollectiblesService {
  
  // Get all collectibles for the current user
  static async getUserCollectibles(userId) {
    try {
      const { data, error } = await supabase?.from('collectibles')?.select(`
          *,
          collection:collections(id, name),
          images:collectible_images(id, image_url, caption, is_primary, display_order)
        `)?.eq('user_id', userId)?.order('created_at', { ascending: false });

      if (error) {
        throw error;
      }

      return { data: data || [], error: null };
    } catch (error) {
      return { data: [], error };
    }
  }

  // Get collectibles by collection
  static async getCollectiblesByCollection(collectionId) {
    try {
      const { data, error } = await supabase?.from('collectibles')?.select(`
          *,
          images:collectible_images(id, image_url, caption, is_primary, display_order)
        `)?.eq('collection_id', collectionId)?.order('created_at', { ascending: false });

      if (error) {
        throw error;
      }

      return { data: data || [], error: null };
    } catch (error) {
      return { data: [], error };
    }
  }

  // Alias for getCollectiblesByCollection - used by collection-detail page
  static async getCollectionItems(collectionId) {
    return CollectiblesService?.getCollectiblesByCollection(collectionId);
  }

  // Get single collectible with full details
  static async getCollectible(collectibleId) {
    try {
      const { data, error } = await supabase?.from('collectibles')?.select(`
          *,
          collection:collections(id, name, description),
          images:collectible_images(id, image_url, caption, is_primary, display_order)
        `)?.eq('id', collectibleId)?.single();

      if (error) {
        throw error;
      }

      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  }

  // Create new collectible
  static async createCollectible(collectibleData) {
    try {
      const { data, error } = await supabase?.from('collectibles')?.insert([collectibleData])?.select()?.single();

      if (error) {
        throw error;
      }

      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  }

  // Update collectible
  static async updateCollectible(collectibleId, updates) {
    try {
      const { data, error } = await supabase?.from('collectibles')?.update({
          ...updates,
          updated_at: new Date()?.toISOString()
        })?.eq('id', collectibleId)?.select()?.single();

      if (error) {
        throw error;
      }

      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  }

  // Delete collectible
  static async deleteCollectible(collectibleId) {
    try {
      const { error } = await supabase?.from('collectibles')?.delete()?.eq('id', collectibleId);

      if (error) {
        throw error;
      }

      return { error: null };
    } catch (error) {
      return { error };
    }
  }

  // Get featured collectibles for dashboard
  static async getFeaturedCollectibles(userId, limit = 6) {
    try {
      const { data, error } = await supabase?.from('collectibles')?.select(`
          *,
          collection:collections(id, name),
          images:collectible_images(id, image_url, caption, is_primary)
        `)?.eq('user_id', userId)?.eq('is_featured', true)?.order('current_value', { ascending: false })?.limit(limit);

      if (error) {
        throw error;
      }

      return { data: data || [], error: null };
    } catch (error) {
      return { data: [], error };
    }
  }

  // Get collectibles by type/category
  static async getCollectiblesByType(userId, collectibleType) {
    try {
      const { data, error } = await supabase?.from('collectibles')?.select(`
          *,
          collection:collections(id, name),
          images:collectible_images(id, image_url, caption, is_primary)
        `)?.eq('user_id', userId)?.eq('collectible_type', collectibleType)?.order('current_value', { ascending: false });

      if (error) {
        throw error;
      }

      return { data: data || [], error: null };
    } catch (error) {
      return { data: [], error };
    }
  }

  // Search collectibles
  static async searchCollectibles(userId, searchQuery) {
    try {
      const { data, error } = await supabase?.from('collectibles')?.select(`
          *,
          collection:collections(id, name),
          images:collectible_images(id, image_url, caption, is_primary)
        `)?.eq('user_id', userId)?.or(`name.ilike.%${searchQuery}%, description.ilike.%${searchQuery}%, brand.ilike.%${searchQuery}%`)?.order('created_at', { ascending: false });

      if (error) {
        throw error;
      }

      return { data: data || [], error: null };
    } catch (error) {
      return { data: [], error };
    }
  }

  // Get dashboard statistics
  static async getDashboardStats(userId) {
    try {
      // Get total statistics
      const { data: stats, error: statsError } = await supabase?.rpc('get_user_collection_stats', { user_uuid: userId });

      if (statsError) {
        // If RPC function doesn't exist, calculate manually
        const { data: collectibles, error: collectiblesError } = await supabase?.from('collectibles')?.select('current_value, purchase_price, collectible_type')?.eq('user_id', userId);

        if (collectiblesError) {
          throw collectiblesError;
        }

        const totalValue = collectibles?.reduce((sum, item) => sum + (parseFloat(item?.current_value) || 0), 0) || 0;
        const totalCost = collectibles?.reduce((sum, item) => sum + (parseFloat(item?.purchase_price) || 0), 0) || 0;
        const totalGrowth = totalCost > 0 ? ((totalValue - totalCost) / totalCost) * 100 : 0;

        // Group by type
        const typeStats = collectibles?.reduce((acc, item) => {
          const type = item?.collectible_type || 'other';
          if (!acc?.[type]) {
            acc[type] = { count: 0, value: 0 };
          }
          acc[type].count += 1;
          acc[type].value += parseFloat(item?.current_value) || 0;
          return acc;
        }, {});

        return {
          data: {
            totalItems: collectibles?.length || 0,
            totalValue: totalValue,
            totalGrowth: totalGrowth,
            typeBreakdown: typeStats
          },
          error: null
        };
      }

      return { data: stats, error: null };
    } catch (error) {
      return { data: null, error };
    }
  }

  // Add image to collectible
  static async addCollectibleImage(collectibleId, imageData) {
    try {
      const { data, error } = await supabase?.from('collectible_images')?.insert([{
          collectible_id: collectibleId,
          ...imageData
        }])?.select()?.single();

      if (error) {
        throw error;
      }

      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  }

  // Set primary image
  static async setPrimaryImage(collectibleId, imageId) {
    try {
      // First, unset all primary images for this collectible
      await supabase?.from('collectible_images')?.update({ is_primary: false })?.eq('collectible_id', collectibleId);

      // Set the selected image as primary
      const { data, error } = await supabase?.from('collectible_images')?.update({ is_primary: true })?.eq('id', imageId)?.select()?.single();

      if (error) {
        throw error;
      }

      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  }

  // Remove image from collectible
  static async removeCollectibleImage(imageId) {
    try {
      const { error } = await supabase?.from('collectible_images')?.delete()?.eq('id', imageId);

      if (error) {
        throw error;
      }

      return { error: null };
    } catch (error) {
      return { error };
    }
  }
}

export default CollectiblesService;